import type { Node, Edge } from 'reactflow';

/** Source Spines SP-2/SP-3b: mark peer_response nodes that descend from a
 *  source-card spine root (blueprint_intent | artifact), so the renderer +
 *  collapse layout can discriminate them from squad peer_responses (which share
 *  the peer_response kind). Mirrors positionIntentSpine's subtree edge-walk.
 *  Mutates node.data in place; idempotent.
 *  SP-3b: loops EVERY source-card root (was a single .find → only the first
 *  spine in a mixed blueprint+artifact session got stamped), stamping each
 *  subtree with its OWN kind and stopping at nested spine roots (a nested fork
 *  owns its own subtree, mirroring computeIntentSpineMembers). */
export function stampSpineContext(nodes: Node[], edges: Edge[]): void {
  const roots = nodes.filter(
    (n) => n.type === 'spine_root_marker'
      && (((n.data as any)?.spawnSource === 'blueprint_intent')
        || ((n.data as any)?.spawnSource === 'artifact')),
  );
  if (!roots.length) return;
  const childrenByParent = new Map<string, string[]>();
  for (const ed of edges) {
    if (!childrenByParent.has(ed.source)) childrenByParent.set(ed.source, []);
    childrenByParent.get(ed.source)!.push(ed.target);
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const root of roots) {
    const kind = (root.data as any).spawnSource;   // stamp the ACTUAL kind
    const seen = new Set<string>();
    const stack = [root.id];
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = byId.get(id);
      // Stop at a DIFFERENT nested spine root — it owns its own subtree.
      if (id !== root.id && node?.type === 'spine_root_marker') continue;
      if (node && node.type === 'peer_response') {
        (node.data as any).spineSpawnSource = kind;
      }
      for (const c of childrenByParent.get(id) ?? []) if (!seen.has(c)) stack.push(c);
    }
  }
}
